// Utility to render a thumbnail PNG data URL from a PDF URL + page + rect
// Uses pdfjs-dist directly to render only the specified region for efficiency

import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import {
  getDocument,
  GlobalWorkerOptions,
  version as pdfjsVersion,
} from 'pdfjs-dist';

export type PdfRect = { x: number; y: number; width: number; height: number };

function ensurePdfWorkerConfigured(): void {
  // Configure worker to load from CDN to avoid bundler-specific imports
  GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;
}

async function loadPdf(url: string): Promise<PDFDocumentProxy> {
  // First try normal loading (range requests enabled)
  try {
    const task = getDocument({ url });
    return await task.promise;
  } catch (e) {
    // Retry without range requests – helps when S3 CORS for Range is not configured
    const task = getDocument({ url, disableRange: true });
    return await task.promise;
  }
}

export async function renderTemplateThumbnailFromPdfUrl(
  pdfUrl: string,
  pageNumber: number,
  rect: PdfRect,
  opts?: { longestSidePx?: number; minScale?: number; maxScale?: number }
): Promise<string> {
  ensurePdfWorkerConfigured();

  const { longestSidePx = 64, minScale = 0.5, maxScale = 6 } = opts ?? {};

  const pdf: PDFDocumentProxy = await loadPdf(pdfUrl);
  try {
    const page: PDFPageProxy = await pdf.getPage(Math.max(1, pageNumber));

    // Compute a scale such that the cropped template renders to roughly the target size
    const desiredScale = longestSidePx / Math.max(rect.width, rect.height);
    const scale = Math.max(minScale, Math.min(maxScale, desiredScale));

    // Respect page rotation; PageViewport handles rotation + y-flip
    const rotation = (page.rotate || 0) % 360;
    const viewport = page.getViewport({ scale, rotation });

    // Transform PDF-space rectangle to viewport/canvas-space rectangle
    // convertToViewportRectangle expects [xMin, yMin, xMax, yMax] in PDF coords (origin bottom-left)
    // and returns [x1, y1, x2, y2] in viewport coords (origin top-left), with rotation applied.
    const rectPoints = viewport.convertToViewportRectangle([
      rect.x,
      rect.y,
      rect.x + rect.width,
      rect.y + rect.height,
    ]) as [number, number, number, number];

    const xMin = Math.min(rectPoints[0], rectPoints[2]);
    const yMin = Math.min(rectPoints[1], rectPoints[3]);
    const xMax = Math.max(rectPoints[0], rectPoints[2]);
    const yMax = Math.max(rectPoints[1], rectPoints[3]);

    let x0 = Math.floor(xMin);
    let y0 = Math.floor(yMin);
    let tw = Math.ceil(xMax - xMin);
    let th = Math.ceil(yMax - yMin);

    // Clamp to viewport bounds
    const vw = Math.floor(viewport.width);
    const vh = Math.floor(viewport.height);
    x0 = Math.max(0, Math.min(x0, vw));
    y0 = Math.max(0, Math.min(y0, vh));
    if (x0 + tw > vw) tw = Math.max(1, vw - x0);
    if (y0 + th > vh) th = Math.max(1, vh - y0);
    if (tw < 1 || th < 1) {
      const tiny = document.createElement('canvas');
      tiny.width = 1;
      tiny.height = 1;
      return tiny.toDataURL('image/png');
    }

    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context not available');

    const renderTask = page.render({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      canvasContext: ctx as any,
      viewport,
      transform: [1, 0, 0, 1, -x0, -y0],
      background: 'rgba(255,255,255,1)',
    });
    await renderTask.promise;

    return canvas.toDataURL('image/png');
  } finally {
    try {
      pdf.cleanup();
      await pdf.destroy();
    } catch (_) {
      // ignore
    }
  }
}
